import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/api';
import { useFormik } from 'formik';
import * as yup from "yup";
import { useToken } from '../components/TokenProvider';
import { useUser } from '../components/UserContext';
import TabTitle from '../components/TabTitle';
import { EyeFill, EyeSlashFill } from 'react-bootstrap-icons';

export default function AdminLogin() {
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const { setToken } = useToken(); 
    const { setUser } = useUser();

    const loginEmailValidationSchema = yup.object({
        email: yup
            .string("Enter email address")
            .email("Enter a valid email")
            .required("Email address is required"),
        password: yup
            .string("Enter your password")
            .min(8, "Password should be of minimum 8 characters length")
            .required("Password is required"),
    });

    const forgotPasswordEmailValidationSchema = yup.object({
      email: yup
        .string("Enter email address")
        .email("Enter a valid email")
        .required("Email address is required"),
    });

    const loginEmailFormik = useFormik({
        initialValues: {
            email: "",
            password: "",
        },
        validationSchema: loginEmailValidationSchema,
        onSubmit: async (values) => {
            const { email, password } = values;
            try {
                setIsSubmitting(true);
                const response = await api.post('/token/', { email, password });
                const { access, refresh, message: responseMessage } = response.data;
                
                if (!access || !refresh) {
                    console.warn("Access or refresh token missing in response!");
                    setMessage("Login failed, no token received.");
                    return;
                }
    
                localStorage.setItem('accessToken', access);
                localStorage.setItem('refreshToken', refresh);
                console.log("Tokens saved successfully!", access, refresh);
    
                setIsSubmitting(false);
                setMessage(responseMessage || "Login successful!");
                setUser(response.data);
    
                setTimeout(() => {
                    console.log("Stored Token Before Redirect:", localStorage.getItem("accessToken"));
                    navigate('/admin/assessments');
                }, 500);
            } catch (error) {
                setIsSubmitting(false);
                setMessage(error.response?.data?.detail || 'An error occurred. Please try again.');
            }
        }
    });

    const forgotPasswordFormik = useFormik({
      initialValues: {
        email: "",
      },
      validationSchema: forgotPasswordEmailValidationSchema,
      onSubmit: async (values) => {
        const { email } = values;
        setMessage('');
        setError('');
        setIsSubmitting(true);
        try {
          const response = await api.post('/assessment/forget_password/', { email });
          const data = await response.data;

          if (response.status === 200) {
            setMessage('Password reset link sent to your email if it exists in our system.');
          } else {
            setError(data.message || 'Something went wrong. Please try again.');
          }
        } catch (err) {
          setError(err.response?.data?.message || 'Network error. Please try again later.');
        } finally {
          setIsSubmitting(false);
        }
      },
    });

    const handleForgotPasswordClick = () => {
      setShowForgotPassword(true);
      setMessage('');
      setError('');
      forgotPasswordFormik.resetForm();
    };

    const handleBackToLoginClick = () => {
      setShowForgotPassword(false);
      setMessage('');
      setError('');
      loginEmailFormik.resetForm();
    };

    return (
        <div>
            <TabTitle title="Login - Admin" />
            <div className="d-flex justify-content-center align-items-center full-height-background">
                <div className="col-10 p-sm-8 col-md-5 col-lg-5 col-xl-4" style={{ opacity: "0.85" }}>
                    <div className="card p-4 p-md-5 p-sm-3 my-4">
                        {message && <div className="mt-3 alert alert-info">{message}</div>}
                        {error && <div className="mt-3 alert alert-danger">{error}</div>}

                        {!showForgotPassword ? (
                          // Login Form
                          <form className='login-form'>
                              <h2 className='mb-4 mb-sm-5 text-center fw-bold'>Login</h2>
                              <div className="mb-3">
                                  <label htmlFor="email" className="form-label">Email<span className="text-danger">*</span></label>
                                  <input
                                      type="email"
                                      className="form-control forminput-custom"
                                      id="email"
                                      placeholder="name@example.com"
                                      value={loginEmailFormik?.values?.email}
                                      name='email'
                                      onBlur={loginEmailFormik.handleBlur}
                                      onChange={loginEmailFormik.handleChange}
                                      error={
                                          loginEmailFormik?.touched?.email &&
                                          Boolean(loginEmailFormik?.errors?.email)
                                      }
                                      required
                                  />
                                  {loginEmailFormik?.touched?.email &&
                                      <small id="passwordHelpBlock" class="form-text text-danger">
                                          {loginEmailFormik.errors.email}
                                      </small>}
                              </div>
                              <div className="mb-3">
                                  <label htmlFor="password" className="form-label">Password<span className="text-danger">*</span></label>
                                  <div className="input-group">
                                      <input
                                          type={showPassword ? "text" : "password"}
                                          className="form-control forminput-custom"
                                          id="password"
                                          placeholder="Enter your password"
                                          value={loginEmailFormik?.values?.password}
                                          name='password'
                                          onBlur={loginEmailFormik.handleBlur}
                                          onChange={loginEmailFormik.handleChange}
                                          error={
                                              loginEmailFormik?.touched?.password &&
                                              Boolean(loginEmailFormik?.errors?.password)
                                          }
                                          required
                                      />
                                        <button
                                            className="btn btn-outline-secondary"
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            style={{ width: '50px', height: '38px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                                        >
                                            {showPassword ? (
                                                <EyeFill color="#000" />
                                            ) : (
                                                <EyeSlashFill color="#000" />
                                            )}
                                      </button>
                                  </div>
                                  {loginEmailFormik?.touched?.password &&
                                      <small id="passwordHelpBlock" class="form-text text-danger">
                                          {loginEmailFormik.errors.password}
                                      </small>}
                              </div>
                              <div
                                  style={{
                                      textAlign: "right", marginBottom: '10px',
                                      fontSize: { xs: "12px", sm: "14px", md: "14px" },
                                      fontWeight: "400",
                                      cursor: "pointer",
                                  }}
                                  onClick={handleForgotPasswordClick}
                              >
                                  Forgot Password?
                              </div>
                              <div className="d-grid">
                                  <button type="submit" className="btn btn-success" disabled={isSubmitting} onClick={loginEmailFormik.handleSubmit}>
                                      {isSubmitting ? <><span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                          <span class="sr-only">Submitting...</span></> : "Submit"}
                                  </button>
                              </div>
                          </form>
                        ) : (
                          // Forgot Password Form
                          <form onSubmit={forgotPasswordFormik.handleSubmit} className='login-form'>
                            <h2 className='mb-4 text-center fw-bold'>Forgot Password</h2>
                            <p className="text-center">Enter your email address to reset your password.</p>
                            <div className="mb-4">
                              <label htmlFor="email" className="form-label">Email<span className="text-danger">*</span></label>
                              <input
                                type="email"
                                className="form-control forminput-custom"
                                id="email"
                                name="email"
                                placeholder="name@example.com"
                                value={forgotPasswordFormik.values.email}
                                onChange={forgotPasswordFormik.handleChange}
                                onBlur={forgotPasswordFormik.handleBlur}
                                required
                              />
                              {forgotPasswordFormik.touched.email && forgotPasswordFormik.errors.email && (
                                <small id="emailHelpBlock" className="form-text text-danger">
                                  {forgotPasswordFormik.errors.email}
                                </small>
                              )}
                            </div>
                            <div className="d-grid">
                              <button type="submit" className="btn btn-success" disabled={isSubmitting}>
                                {isSubmitting ? (
                                  <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  <span className="sr-only">Sending...</span></>
                                ) : (
                                  "Submit"
                                )}
                              </button>
                            </div>
                            <div className="mt-3 text-center">
                              <Link href="/admin/login" className="text-primary" style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={handleBackToLoginClick}>Back to Login</Link>
                            </div>
                          </form>
                        )}

                        {!showForgotPassword && (
                          <div className="mt-3 text-center">
                                <span>Don't have an account? </span>
                                <Link to="/admin/signup" className="text-primary" style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                                    Sign Up
                                </Link>
                          </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}